import { useEffect, useRef, useState } from "react";
import "./App.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const DEFAULT_SHELF_IMAGE_URL = `${API_BASE}/outputs/kallax_seed0.png?v=rectangular-shadow-1`;

const styles = {
  page: {
    display: "flex",
    height: "100vh",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    background: "#eef2f7",
    color: "#1f2937",
  },

  sidebar: {
    width: "340px",
    flexShrink: 0,
    background: "#f8fafc",
    padding: "24px",
    borderRight: "1px solid #d7dee8",
    overflowY: "auto",
    boxSizing: "border-box",
  },

  rightSidebar: {
    width: "340px",
    flexShrink: 0,
    background: "#f8fafc",
    padding: "24px",
    borderLeft: "1px solid #d7dee8",
    overflowY: "auto",
    boxSizing: "border-box",
  },

  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "32px",
    overflowY: "auto",
    boxSizing: "border-box",
  },

  title: {
    margin: "0 0 6px 0",
    fontSize: "34px",
    fontWeight: 750,
    letterSpacing: "-0.03em",
  },

  subtitle: {
    margin: "0 0 28px 0",
    color: "#64748b",
    fontSize: "15px",
  },

  sectionTitle: {
    margin: "0 0 14px 0",
    fontSize: "18px",
    fontWeight: 700,
    color: "#111827",
  },

  section: {
    marginBottom: "26px",
  },

  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "10px",
    fontSize: "15px",
  },

  parameterLabel: {
    display: "block",
    fontSize: "14px",
    fontWeight: 650,
    marginBottom: "6px",
    color: "#334155",
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 10px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "white",
    fontSize: "14px",
    outline: "none",
  },

  helperText: {
    marginTop: "4px",
    fontSize: "12px",
    color: "#64748b",
  },

  button: {
    padding: "18px 42px",
    fontSize: "19px",
    fontWeight: 700,
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    color: "white",
    border: "none",
    borderRadius: "14px",
    cursor: "pointer",
    boxShadow: "0 12px 28px rgba(37, 99, 235, 0.28)",
    transition:
      "transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease",
  },

  buttonDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
    boxShadow: "none",
  },

  secondaryButton: {
    padding: "9px 12px",
    fontSize: "13px",
    fontWeight: 700,
    borderRadius: "9px",
    border: "1px solid #cbd5e1",
    background: "white",
    color: "#334155",
    cursor: "pointer",
  },

  githubButton: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "12px",
    border: "1px solid #111827",
    background: "#111827",
    color: "white",
    fontWeight: 750,
    cursor: "pointer",
    fontSize: "14px",
  },

  leaderboardShortcut: {
    width: "100%",
    marginTop: "10px",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #94a3b8",
    background: "white",
    color: "#334155",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: "14px",
    textAlign: "left",
  },

  userCard: {
    background: "white",
    border: "1px solid #dbe3ef",
    borderRadius: "14px",
    padding: "14px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
  },

  avatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
  },

  renderStatus: {
    width: "min(720px, 100%)",
    boxSizing: "border-box",
    margin: "10px auto 0",
    padding: "8px 10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    border: "1px solid #bae6fd",
    borderRadius: "8px",
    background: "#f0f9ff",
    color: "#334155",
  },

  renderStatusError: {
    borderColor: "#fecaca",
    background: "#fef2f2",
  },

  renderStatusMessage: {
    flex: 1,
    minWidth: 0,
    fontSize: "13px",
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },

  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "999px",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 750,
    background: "#e0f2fe",
    color: "#075985",
    whiteSpace: "nowrap",
  },

  statusBadgeError: {
    background: "#fee2e2",
    color: "#991b1b",
  },

  resultGrid: {
    marginTop: "28px",
    width: "min(900px, 100%)",
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "24px",
  },

  resultCard: {
    background: "white",
    border: "1px solid #dbe3ef",
    borderRadius: "18px",
    padding: "20px",
    boxShadow: "0 14px 34px rgba(15, 23, 42, 0.07)",
  },

  shelfImageFrame: {
    position: "relative",
    width: "min(720px, 100%)",
    margin: "0 auto",
  },

  resultTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    marginBottom: "14px",
  },

  resultTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 750,
  },

  subtleBadge: {
    borderRadius: "999px",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 700,
    background: "#e2e8f0",
    color: "#475569",
    whiteSpace: "nowrap",
  },

  shelfImage: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: "14px",
    background: "white",
  },

  seedOverlay: {
    position: "absolute",
    right: "12px",
    bottom: "12px",
    padding: "5px 8px",
    borderRadius: "6px",
    background: "rgba(15, 23, 42, 0.72)",
    color: "white",
    fontSize: "12px",
    fontWeight: 650,
    fontVariantNumeric: "tabular-nums",
    pointerEvents: "none",
  },

  ycbGrid: {
    display: "flex",
    gap: "14px",
    flexWrap: "wrap",
  },

  ycbImage: {
    maxWidth: "160px",
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    background: "white",
  },

  resultButtons: {
    marginTop: "18px",
    padding: "16px",
    borderRadius: "14px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
  },

  slotGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    width: "100%",
    borderTop: "1px solid #dbe3ef",
    borderLeft: "1px solid #dbe3ef",
    marginTop: "12px",
  },

  slotCell: {
    minWidth: 0,
    minHeight: "82px",
    padding: "8px",
    boxSizing: "border-box",
    borderRight: "1px solid #dbe3ef",
    borderBottom: "1px solid #dbe3ef",
  },

  slotChoice: {
    padding: "5px 2px",
    minWidth: 0,
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#cbd5e1",
    background: "white",
    color: "#334155",
    fontSize: "11px",
    fontWeight: 700,
    cursor: "pointer",
  },

  successButton: {
    padding: "10px 14px",
    borderRadius: "10px",
    border: "1px solid #16a34a",
    background: "#16a34a",
    color: "white",
    fontWeight: 750,
    cursor: "pointer",
    marginRight: "10px",
  },

  failureButton: {
    padding: "10px 14px",
    borderRadius: "10px",
    border: "1px solid #dc2626",
    background: "#dc2626",
    color: "white",
    fontWeight: 750,
    cursor: "pointer",
  },

  insertHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
  },

  categoryCard: {
    marginTop: "14px",
    padding: "14px",
    background: "white",
    border: "1px solid #dbe3ef",
    borderRadius: "14px",
  },

  categoryTitle: {
    fontWeight: 750,
    color: "#111827",
  },

  insertItem: {
    display: "grid",
    gridTemplateColumns: "20px 1fr auto",
    gap: "10px",
    alignItems: "start",
    padding: "12px 0",
    borderTop: "1px solid #edf2f7",
  },

  insertName: {
    fontWeight: 700,
    lineHeight: 1.25,
  },

  insertMeta: {
    marginTop: "3px",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.35,
  },

  countLabel: {
    fontSize: "12px",
    color: "#64748b",
    whiteSpace: "nowrap",
  },

  countInput: {
    width: "58px",
    marginTop: "4px",
    padding: "7px 6px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    fontSize: "14px",
    boxSizing: "border-box",
  },

  noInsertsBox: {
    marginTop: "18px",
    padding: "14px",
    borderRadius: "14px",
    background: "#fff7ed",
    border: "1px solid #fed7aa",
  },
};

function cleanText(value) {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text || text.toLowerCase() === "nan" || text.toLowerCase() === "none") {
    return "";
  }
  return text;
}

function insertKey(item) {
  return cleanText(item.article_number);
}

function canonicalChallengeType(value) {
  if (typeof value !== "string") return "Open Inserts";
  switch (value.trim().toLowerCase()) {
    case "open inserts": return "Open Inserts";
    case "find object":
    case "find & take object": return "Find Object";
    case "move object": return "Move Object";
    default: return value;
  }
}

function buildInsertState(categories, selectedInserts = null, noInserts = false) {
  const selectedCounts = new Map(
    (selectedInserts || []).map((item) => [
      cleanText(item.article_number),
      Number(item.count) || 1,
    ]),
  );
  const restoringSelection = Array.isArray(selectedInserts);
  const nextChecked = {};
  const nextCounts = {};

  Object.values(categories || {}).forEach((items) => {
    items.forEach((item) => {
      const key = insertKey(item);
      if (!key) return;
      nextChecked[key] =
        !noInserts && (!restoringSelection || selectedCounts.has(key));
      nextCounts[key] = selectedCounts.get(key) ?? 1;
    });
  });

  return { checked: nextChecked, counts: nextCounts };
}

function withCacheBust(url) {
  if (!url) return null;
  return `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
}

function InsertGroupLabels({ configuration }) {
  if (configuration.no_inserts) {
    return <span className="insert-group-label neutral">No inserts</span>;
  }

  if (!configuration.insert_group_snapshot_available) {
    return (
      <span
        className="insert-group-label neutral"
        title="This challenge predates insert-group snapshots"
      >
        Groups unavailable
      </span>
    );
  }

  const completeGroups = configuration.complete_insert_groups || [];
  const partialGroups = configuration.partial_insert_groups || [];
  return (
    <>
      {completeGroups.map((group) => (
        <span
          key={`complete-${group}`}
          className="insert-group-label complete"
          title={`All ${group} inserts were enabled`}
        >
          {group}
        </span>
      ))}
      {partialGroups.map((group) => (
        <span
          key={`partial-${group}`}
          className="insert-group-label partial"
          title={`Only some ${group} inserts were enabled`}
        >
          Partial: {group}
        </span>
      ))}
    </>
  );
}

function LeaderboardTable({ data, scope }) {
  const configurations = data?.configurations || [];
  const participants = data?.participants || [];

  if (configurations.length === 0) {
    return (
      <div className="leaderboard-empty">
        No completed challenge results have been reported yet.
      </div>
    );
  }

  return (
    <div className="leaderboard-table-wrap">
      <table className="leaderboard-table">
        <caption className="visually-hidden">
          {scope === "personal"
            ? "Your most-tested challenge configurations"
            : "The most-tested challenge configurations across all users"}
        </caption>
        <thead>
          <tr>
            <th scope="col">Seed</th>
            <th scope="col">Challenge</th>
            <th scope="col">Configuration</th>
            <th scope="col">Tests</th>
            {scope === "personal" ? (
              <>
                <th scope="col">Successes</th>
                <th scope="col">Failures</th>
                <th scope="col">Success rate</th>
              </>
            ) : (
              <>
                <th scope="col">Overall</th>
                {participants.map((participant) => (
                  <th
                    key={participant.github_login}
                    scope="col"
                    title={participant.github_name || participant.github_login}
                  >
                    @{participant.github_login}
                  </th>
                ))}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {configurations.map((configuration) => (
            <tr key={configuration.configuration_id}>
              <th scope="row">
                <span className="leaderboard-seed">{configuration.seed}</span>
                <span className="leaderboard-insert-groups">
                  <InsertGroupLabels configuration={configuration} />
                </span>
              </th>
              <td>{configuration.challenge_type}</td>
              <td>
                <code>{configuration.configuration_id}</code>
                <span className="leaderboard-config-meta">
                  p={configuration.empty_probability} | {configuration.selected_insert_types}{" "}
                  insert types | {configuration.selected_insert_count} max uses
                </span>
              </td>
              <td>{configuration.trials}</td>
              {scope === "personal" ? (
                <>
                  <td>{configuration.successes}</td>
                  <td>{configuration.failures}</td>
                  <td className="leaderboard-rate">
                    {configuration.success_rate}%
                  </td>
                </>
              ) : (
                <>
                  <td className="leaderboard-rate">
                    {configuration.success_rate}%
                  </td>
                  {participants.map((participant) => {
                    const result = configuration.participants.find(
                      (item) => item.github_login === participant.github_login,
                    );
                    return (
                      <td
                        key={participant.github_login}
                        className="leaderboard-rate"
                      >
                        {result ? `${result.success_rate}% (${result.trials})` : "-"}
                      </td>
                    );
                  })}
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function App() {
  const leaderboardSectionRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [output, setOutput] = useState("");
  const [runState, setRunState] = useState("idle");
  const [lastSeed, setLastSeed] = useState(null);
  const [lastChallengeId, setLastChallengeId] = useState(null);
  const [reportedResult, setReportedResult] = useState(null);
  const [slotResults, setSlotResults] = useState({});
  const [slots, setSlots] = useState([]);
  const [activeChallengeType, setActiveChallengeType] = useState(null);

  const [shelfImage, setShelfImage] = useState(null);
  const [annotatedImage, setAnnotatedImage] = useState(null);
  const [ycbImages, setYcbImages] = useState([]);
  const [ycbObject, setYcbObject] = useState(null);

  const [inserts, setInserts] = useState({});
  const [checked, setChecked] = useState({});
  const [counts, setCounts] = useState({});
  const [noInserts, setNoInserts] = useState(false);

  const [challengeType, setChallengeType] = useState("Open Inserts");

  const [seedInput, setSeedInput] = useState("");
  const [emptyProbInput, setEmptyProbInput] = useState("0.5");

  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [leaderboardScope, setLeaderboardScope] = useState("global");
  const [leaderboards, setLeaderboards] = useState({
    global: null,
    personal: null,
  });
  const [leaderboardLoadingScope, setLeaderboardLoadingScope] = useState(null);
  const [leaderboardError, setLeaderboardError] = useState("");
  const [leaderboardScrollRequest, setLeaderboardScrollRequest] = useState(0);

  useEffect(() => {
    let active = true;

    const loadApplicationState = async () => {
      try {
        const [authResponse, insertsResponse] = await Promise.all([
          fetch(`${API_BASE}/auth/me`, { credentials: "include" }),
          fetch(`${API_BASE}/get-inserts`),
        ]);
        const [authData, insertsData] = await Promise.all([
          authResponse.json(),
          insertsResponse.json(),
        ]);

        const user = authData.authenticated ? authData.user : null;
        const categories =
          insertsData.status === "ok" ? insertsData.categories || {} : {};
        let latestChallenge = null;

        if (user) {
          const latestResponse = await fetch(`${API_BASE}/challenges/latest`, {
            credentials: "include",
          });
          const latestData = await latestResponse.json();
          if (latestData.status === "ok") {
            latestChallenge = latestData.challenge;
          }
        }

        if (!active) return;

        setCurrentUser(user);
        setInserts(categories);

        const insertState = buildInsertState(
          categories,
          latestChallenge?.selected_inserts ?? null,
          latestChallenge?.no_inserts ?? false,
        );
        setChecked(insertState.checked);
        setCounts(insertState.counts);

        if (latestChallenge) {
          setRunState("success");
          setOutput("Restored your latest challenge.");
          setLastSeed(latestChallenge.seed);
          setLastChallengeId(latestChallenge.challenge_id);
          setReportedResult(latestChallenge.result_status);
          setSlotResults(latestChallenge.slot_results || {});
          setSlots(latestChallenge.slots || []);
          setActiveChallengeType(canonicalChallengeType(latestChallenge.challenge_type));
          setShelfImage(withCacheBust(latestChallenge.shelf_image));
          setAnnotatedImage(withCacheBust(latestChallenge.annotated_image));
          setYcbImages(
            (latestChallenge.ycb_images || []).map(withCacheBust).filter(Boolean),
          );
          setYcbObject(latestChallenge.ycb_object);
          setNoInserts(latestChallenge.no_inserts);
          setChallengeType(canonicalChallengeType(latestChallenge.challenge_type));
          setSeedInput(String(latestChallenge.seed));
          setEmptyProbInput(String(latestChallenge.empty_probability));
        }
      } catch (err) {
        if (active) {
          setCurrentUser(null);
          console.error("Could not load application state:", err);
        }
      } finally {
        if (active) setAuthChecked(true);
      }
    };

    loadApplicationState();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (leaderboardScrollRequest === 0) return;
    leaderboardSectionRef.current?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    });
  }, [leaderboardScrollRequest]);

  const loginWithGitHub = () => {
    window.location.href = `${API_BASE}/auth/github/login`;
  };

  const logout = async () => {
    await fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    setCurrentUser(null);
    setRunState("idle");
    setOutput("");
    setLastSeed(null);
    setLastChallengeId(null);
    setReportedResult(null);
    setSlotResults({});
    setSlots([]);
    setActiveChallengeType(null);
    setShelfImage(null);
    setAnnotatedImage(null);
    setYcbImages([]);
    setYcbObject(null);
    setLeaderboardOpen(false);
    setLeaderboardScope("global");
    setLeaderboards((previous) => ({ ...previous, personal: null }));
  };

  const loadLeaderboard = async (scope, force = false) => {
    if (scope === "personal" && !currentUser) return;
    if (!force && leaderboards[scope]) return;

    setLeaderboardLoadingScope(scope);
    setLeaderboardError("");
    try {
      const response = await fetch(
        `${API_BASE}/leaderboards/${scope}?limit=20`,
        {
          credentials: "include",
          cache: "no-store",
        },
      );
      const data = await response.json();
      if (response.ok === false || data.status !== "ok") {
        throw new Error(data.detail || data.message || "Could not load results.");
      }
      setLeaderboards((previous) => ({ ...previous, [scope]: data }));
    } catch (err) {
      setLeaderboardError(err.message);
    } finally {
      setLeaderboardLoadingScope((activeScope) =>
        activeScope === scope ? null : activeScope,
      );
    }
  };

  const openLeaderboard = (scope = currentUser ? "personal" : "global") => {
    setLeaderboardScope(scope);
    setLeaderboardOpen(true);
    setLeaderboardError("");
    loadLeaderboard(scope);
    setLeaderboardScrollRequest((request) => request + 1);
  };

  const toggleLeaderboard = () => {
    if (leaderboardOpen) {
      setLeaderboardOpen(false);
      return;
    }

    openLeaderboard();
  };

  const selectLeaderboardScope = (scope) => {
    setLeaderboardScope(scope);
    setLeaderboardError("");
    loadLeaderboard(scope);
  };

  const toggleAll = (val) => {
    const updated = {};
    Object.keys(checked).forEach((k) => {
      updated[k] = val;
    });
    setChecked(updated);
    if (val) setNoInserts(false);
  };

  const toggleCategory = (cat, val) => {
    const updated = { ...checked };
    (inserts[cat] || []).forEach((item) => {
      updated[insertKey(item)] = val;
    });
    setChecked(updated);
    if (val) setNoInserts(false);
  };

  const toggleItem = (item, val) => {
    setChecked({ ...checked, [insertKey(item)]: val });
    if (val) setNoInserts(false);
  };

  const changeCount = (item, val) => {
    const n = Math.max(0, Math.min(12, parseInt(val || "0", 10)));
    setCounts({ ...counts, [insertKey(item)]: n });
  };

  const handleNoInsertsToggle = (v) => {
    setNoInserts(v);

    if (v) {
      const off = {};
      Object.keys(checked).forEach((k) => {
        off[k] = false;
      });
      setChecked(off);
    } else {
      const on = {};
      Object.keys(checked).forEach((k) => {
        on[k] = true;
      });
      setChecked(on);
    }
  };

  const runChallenge = async () => {
    if (!currentUser) {
      setRunState("error");
      setOutput("Please log in with GitHub before generating a challenge.");
      return;
    }

    setRunState("running");
    setOutput("Generating shelf and challenge data...");
    setLastSeed(null);
    setLastChallengeId(null);
    setReportedResult(null);
    setSlotResults({});
    setSlots([]);
    setActiveChallengeType(null);
    setShelfImage(null);
    setAnnotatedImage(null);
    setYcbImages([]);
    setYcbObject(null);

    try {
      let selectedInserts = [];

      if (!noInserts) {
        Object.values(inserts).forEach((items) => {
          items.forEach((item) => {
            const key = insertKey(item);
            if (checked[key]) {
              selectedInserts.push({
                article_number: item.article_number,
                count: counts[key] ?? 1,
              });
            }
          });
        });
      }

      const payload = {
        selectedInserts,
        seed: seedInput || null,
        empty_probability: emptyProbInput || null,
        noInserts,
        challengeType,
      };

      const res = await fetch(`${API_BASE}/run-challenge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.status === 401) {
        setRunState("error");
        setOutput(
          "Your login session expired. Please log in with GitHub again.",
        );
        setCurrentUser(null);
        return;
      }

      if (data.status === "ok") {
        setRunState("success");
        setLastSeed(data.seed);
        setLastChallengeId(data.challenge_id || null);
        setActiveChallengeType(challengeType);
        setSlots(data.slots || []);
        setSlotResults(data.slot_results || {});
        setOutput(data.stdout || "Generation completed.");

        if (data.shelf_image) {
          setShelfImage(withCacheBust(data.shelf_image));
        }
        if (data.annotated_image) {
          setAnnotatedImage(withCacheBust(data.annotated_image));
        }
        if (data.ycb_images) {
          setYcbImages(data.ycb_images.map(withCacheBust).filter(Boolean));
        }
        if (data.ycb_object) {
          setYcbObject(data.ycb_object);
        }
      } else {
        setRunState("error");
        setOutput(data.stderr || data.detail || data.message || "Unknown error.");
      }
    } catch (err) {
      setRunState("error");
      setOutput(err.message);
    }
  };

  const reportResult = async (resultStatus, slotIndex = null) => {
    if (!lastChallengeId) return;

    try {
      const res = await fetch(
        `${API_BASE}/challenges/${lastChallengeId}/result`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            result_status: resultStatus,
            result_notes: null,
            ...(slotIndex !== null ? { slot_index: slotIndex } : {}),
          }),
        },
      );

      const data = await res.json();

      if (data.status === "ok") {
        setReportedResult(data.result_status);
        setSlotResults(data.slot_results || {});
        setLeaderboards({ global: null, personal: null });
        if (leaderboardOpen) {
          loadLeaderboard(leaderboardScope, true);
        }
      } else {
        setRunState("error");
        setOutput(data.detail || data.message || "Could not store result.");
      }
    } catch (err) {
      setRunState("error");
      setOutput(err.message);
    }
  };

  const statusBadgeStyle =
    runState === "error"
      ? { ...styles.statusBadge, ...styles.statusBadgeError }
      : styles.statusBadge;

  const displayShelfImage =
    annotatedImage || shelfImage || DEFAULT_SHELF_IMAGE_URL;

  const resultBadgeText = annotatedImage
    ? "Move Object annotation"
    : shelfImage
      ? "Current challenge"
      : "Empty preview";

  const generateDisabled = runState === "running" || !authChecked;

  return (
    <div className="app-page" style={styles.page}>
      {/* Left Sidebar */}
      <div className="app-sidebar" style={styles.sidebar}>
        <div style={styles.section}>
          {currentUser ? (
            <div style={styles.userCard}>
              {currentUser.github_avatar_url && (
                <img
                  src={currentUser.github_avatar_url}
                  alt="GitHub avatar"
                  style={styles.avatar}
                />
              )}

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 750,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {currentUser.github_name || currentUser.github_login}
                </div>
                <div style={{ fontSize: 13, color: "#64748b" }}>
                  @{currentUser.github_login}
                </div>
              </div>

              <button onClick={logout} style={styles.secondaryButton}>
                Logout
              </button>
            </div>
          ) : (
            <button onClick={loginWithGitHub} style={styles.githubButton}>
              Login with GitHub
            </button>
          )}

          <button
            type="button"
            onClick={() => openLeaderboard()}
            style={styles.leaderboardShortcut}
          >
            {currentUser ? "View My Results" : "View Global Leaderboard"}
          </button>

          {!authChecked && (
            <div style={{ marginTop: 8, fontSize: 13, color: "#64748b" }}>
              Checking login state...
            </div>
          )}
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Challenge Types</h2>

          <div style={styles.optionRow}>
            <input
              type="radio"
              id="challenge-open"
              name="challenge"
              value="Open Inserts"
              checked={challengeType === "Open Inserts"}
              onChange={() => setChallengeType("Open Inserts")}
            />
            <label htmlFor="challenge-open">Open Inserts</label>
          </div>

          <div style={styles.optionRow}>
            <input
              type="radio"
              id="challenge-find"
              name="challenge"
              value="Find Object"
              checked={challengeType === "Find Object"}
              onChange={() => setChallengeType("Find Object")}
            />
            <label htmlFor="challenge-find">Find Object</label>
          </div>

          <div style={styles.optionRow}>
            <input
              type="radio"
              id="challenge-move"
              name="challenge"
              value="Move Object"
              checked={challengeType === "Move Object"}
              onChange={() => setChallengeType("Move Object")}
            />
            <label htmlFor="challenge-move">Move Object</label>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Parameters</h2>

          <div style={{ marginBottom: 14 }}>
            <label style={styles.parameterLabel}>Seed</label>
            <input
              type="text"
              value={seedInput}
              onChange={(e) => setSeedInput(e.target.value)}
              placeholder="Optional, e.g. 1234"
              style={styles.input}
            />
            <div style={styles.helperText}>
              Leave empty to generate a random seed.
            </div>
          </div>

          <div>
            <label style={styles.parameterLabel}>Empty probability</label>
            <input
              type="text"
              value={emptyProbInput}
              onChange={(e) => setEmptyProbInput(e.target.value)}
              placeholder="Optional, e.g. 0.5"
              style={styles.input}
            />
            <div style={styles.helperText}>Default: 0.5</div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="app-main" style={styles.main}>
        <h1 style={styles.title}>Benchmark Randomizer</h1>
        <p style={styles.subtitle}>
          Generate a randomized KALLAX shelf and prepare a challenge.
        </p>

        <button
          onClick={currentUser ? runChallenge : loginWithGitHub}
          disabled={generateDisabled}
          style={{
            ...styles.button,
            ...(generateDisabled ? styles.buttonDisabled : {}),
          }}
        >
          {runState === "running"
            ? "Generating..."
            : !authChecked
              ? "Checking Login..."
              : currentUser
                ? "Generate Challenge"
                : "Login with GitHub to Generate"}
        </button>

        <div style={styles.resultGrid}>
          <div style={styles.resultCard}>
            <div style={styles.resultTitleRow}>
              <h3 style={styles.resultTitle}>Generated Shelf</h3>
              <span style={styles.subtleBadge}>{resultBadgeText}</span>
            </div>

            <div style={styles.shelfImageFrame}>
              <img
                src={displayShelfImage}
                alt="Shelf"
                style={styles.shelfImage}
              />
              {lastSeed !== null && (
                <span style={styles.seedOverlay}>Seed {lastSeed}</span>
              )}
            </div>

            {(runState === "running" || runState === "error") && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  ...styles.renderStatus,
                  ...(runState === "error" ? styles.renderStatusError : {}),
                }}
              >
                <span style={styles.renderStatusMessage}>{output}</span>
                <span style={statusBadgeStyle}>
                  {runState === "running" ? "Running" : "Error"}
                </span>
              </div>
            )}

            {lastChallengeId && activeChallengeType === "Open Inserts" && slots.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h3 style={styles.resultTitle}>Shelf slots</h3>
                <div style={styles.slotGrid}>
                  {[2, 1, 0].flatMap((row) =>
                    [0, 1, 2, 3].map((col) => {
                      const slotIndex = row * 4 + col;
                      const slot = slots.find((item) => item.slot_index === slotIndex);
                      const article = slot?.article_number || "EMPTY";
                      const occupied = article.toUpperCase() !== "EMPTY";
                      const selected = slotResults[String(slotIndex)];
                      return (
                        <div key={slotIndex} style={styles.slotCell}>
                          <div style={{ fontSize: 11, color: "#64748b" }}>
                            Slot {row},{col}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 700, overflowWrap: "anywhere" }}>
                            {occupied ? article : "Empty"}
                          </div>
                          {occupied && (
                            <div className="slot-choices" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", marginTop: 6 }}>
                              {["success", "failure"].map((status) => (
                                <button
                                  key={status}
                                  type="button"
                                  aria-label={`Slot ${row},${col} ${status}`}
                                  aria-pressed={selected === status}
                                  onClick={() => reportResult(status, slotIndex)}
                                  style={{
                                    ...styles.slotChoice,
                                    ...(selected === status ? {
                                      background: status === "success" ? "#dcfce7" : "#fee2e2",
                                      borderColor: status === "success" ? "#16a34a" : "#dc2626",
                                      color: status === "success" ? "#166534" : "#991b1b",
                                    } : {}),
                                  }}
                                >
                                  {status === "success" ? "Pass" : "Fail"}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }),
                  )}
                </div>
              </div>
            )}

            {lastChallengeId && activeChallengeType !== "Open Inserts" && (
              <div style={styles.resultButtons}>
                <div style={{ fontWeight: 750, marginBottom: 8 }}>
                  Report challenge result
                </div>

                {reportedResult ? (
                  <div style={{ color: "#475569", fontSize: 14 }}>
                    Stored result:{" "}
                    <strong>
                      {reportedResult === "success" ? "Success" : "Failure"}
                    </strong>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => reportResult("success")}
                      style={styles.successButton}
                    >
                      Challenge successful
                    </button>

                    <button
                      onClick={() => reportResult("failure")}
                      style={styles.failureButton}
                    >
                      Challenge failed
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {ycbObject && (
            <div style={styles.resultCard}>
              <h3 style={styles.resultTitle}>Chosen YCB Object: {ycbObject}</h3>

              {ycbImages.length > 0 && (
                <div style={styles.ycbGrid}>
                  {ycbImages.map((u, i) => (
                    <img
                      key={i}
                      src={u}
                      alt={`YCB ${i}`}
                      style={styles.ycbImage}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          <section
            ref={leaderboardSectionRef}
            className={`leaderboard-shell${leaderboardOpen ? " is-open" : ""}`}
          >
            <button
              type="button"
              className="leaderboard-disclosure"
              aria-expanded={leaderboardOpen}
              aria-controls="leaderboard-panel"
              onClick={toggleLeaderboard}
            >
              <span>Results Leaderboards</span>
              <span>{leaderboardOpen ? "Hide" : "View"}</span>
            </button>

            {leaderboardOpen && (
              <div id="leaderboard-panel" className="leaderboard-panel">
                <div className="leaderboard-heading-row">
                  <h3>
                    {leaderboardScope === "personal"
                      ? "My Results"
                      : "Global Results"}
                  </h3>
                  <button
                    type="button"
                    className="leaderboard-refresh"
                    onClick={() => loadLeaderboard(leaderboardScope, true)}
                    disabled={leaderboardLoadingScope === leaderboardScope}
                  >
                    Refresh
                  </button>
                </div>

                {currentUser && (
                  <div className="leaderboard-tabs" role="tablist" aria-label="Result scope">
                    {["personal", "global"].map((scope) => (
                      <button
                        key={scope}
                        type="button"
                        role="tab"
                        aria-selected={leaderboardScope === scope}
                        className={leaderboardScope === scope ? "active" : ""}
                        onClick={() => selectLeaderboardScope(scope)}
                      >
                        {scope === "personal" ? "My Results" : "Global"}
                      </button>
                    ))}
                  </div>
                )}

                {leaderboardLoadingScope === leaderboardScope && (
                  <div className="leaderboard-message" role="status">
                    Loading results...
                  </div>
                )}

                {leaderboardError && leaderboardLoadingScope !== leaderboardScope && (
                  <div className="leaderboard-error" role="alert">
                    <span>{leaderboardError}</span>
                    <button
                      type="button"
                      onClick={() => loadLeaderboard(leaderboardScope, true)}
                    >
                      Try again
                    </button>
                  </div>
                )}

                {!leaderboardError &&
                  leaderboardLoadingScope !== leaderboardScope && (
                    <LeaderboardTable
                      data={leaderboards[leaderboardScope]}
                      scope={leaderboardScope}
                    />
                  )}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Right Sidebar */}
      <div className="app-right-sidebar" style={styles.rightSidebar}>
        <div style={styles.insertHeaderRow}>
          <h2 style={{ ...styles.sectionTitle, marginBottom: 0 }}>Inserts</h2>
        </div>

        <div style={styles.optionRow}>
          <input
            type="checkbox"
            onChange={(e) => toggleAll(e.target.checked)}
            checked={
              !noInserts &&
              Object.values(checked).length > 0 &&
              Object.values(checked).every((v) => v)
            }
          />
          <label>All Inserts</label>
        </div>

        {Object.entries(inserts).map(([cat, items]) => (
          <div key={cat} style={styles.categoryCard}>
            <div style={styles.optionRow}>
              <input
                type="checkbox"
                disabled={noInserts}
                onChange={(e) => toggleCategory(cat, e.target.checked)}
                checked={
                  !noInserts &&
                  items.length > 0 &&
                  items.every((item) => checked[insertKey(item)])
                }
              />
              <label style={styles.categoryTitle}>{cat}</label>
            </div>

            <div>
              {items.map((item) => {
                const key = insertKey(item);
                const colorText = cleanText(item.item_color);

                return (
                  <div key={key} style={styles.insertItem}>
                    <input
                      type="checkbox"
                      disabled={noInserts}
                      checked={checked[key] || false}
                      onChange={(e) => toggleItem(item, e.target.checked)}
                      style={{ marginTop: 3 }}
                    />

                    <div>
                      <div style={styles.insertName}>{item.item_name}</div>
                      <div style={styles.insertMeta}>
                        {colorText && (
                          <>
                            {colorText}
                            <br />
                          </>
                        )}
                        {item.article_number}
                      </div>
                    </div>

                    <div>
                      <label style={styles.countLabel}>
                        max uses
                        <br />
                        <input
                          type="number"
                          min={0}
                          max={12}
                          disabled={noInserts || !checked[key]}
                          value={counts[key] ?? 1}
                          onChange={(e) => changeCount(item, e.target.value)}
                          style={styles.countInput}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div style={styles.noInsertsBox}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={noInserts}
              onChange={(e) => handleNoInsertsToggle(e.target.checked)}
            />
            <span>
              <strong>No Inserts</strong>
              <br />
              <span style={{ fontSize: 13, color: "#9a3412" }}>
                Render the shelf without any insert objects.
              </span>
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}

export default App;
